import {
  Accordion,
  Box,
  Form,
  KeyboardDatePicker,
  MuiLocalizationProvider,
  Typography,
  MuiAdapterDateFns,
  TextField,
  TextFieldProps,
} from 'cs-kevlar-react'
import { Formik } from 'formik'
import * as React from 'react'
import {
  EditModalActionBar,
  ScopesList,
  TokenCreationExplanation,
} from 'components'
import {
  makeStyles,
  createStyles,
  Theme,
} from 'cs-kevlar-react/lib/es/theming/theme'
import { ApiTokenConfigurationViewModel } from '../../api'
import { getAllScopes } from 'utils'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    form: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    formFieldContainer: {
      flexGrow: 1,
      minHeight: 0,
      overflowY: 'auto',
      padding: theme.spacing(8),
    },
    spacer: {
      flexGrow: 1,
      minHeight: 0,
    },
    input: {
      minWidth: 0,
    }
  }),
)

const getDateFromDayOffset = (dayOffset: number | undefined) => {
  if (!dayOffset) return null
  const newDate = new Date()
  newDate.setDate(newDate.getDate() + dayOffset)
  return newDate
}

export interface FormikFormValues {
  expiresOn: Date | null
}

interface CreateTokenProps {
  apiTokenConfig: ApiTokenConfigurationViewModel | null
  onClose: () => void
  onCreate: (formik: FormikFormValues) => void
}

export const CreateToken: React.FunctionComponent<CreateTokenProps> = (
  props,
) => {
  const classes = useStyles({})
  const { apiTokenConfig, onClose, onCreate } = props

  const DISALLOWED_STANDARD_API_TOKEN_SCOPES = getAllScopes()
    .map((scope) => parseInt(scope.key))
    .filter(
      (scope) =>
        scope !== 0 &&
        apiTokenConfig !== null &&
        !apiTokenConfig.defaultScopes.includes(scope),
    )

  return (
    <Formik
      initialValues={{
        expiresOn: getDateFromDayOffset(apiTokenConfig?.defaultExpirationDays),
      }}
      enableReinitialize
      onSubmit={onCreate}
    >
      {(formikProps) => (
        <Form onSubmit={formikProps.handleSubmit} className={classes.form}>
          <Box className={classes.formFieldContainer}>
            <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
              <KeyboardDatePicker
                label="Expires On"
                inputFormat="MM/dd/yyyy"
                disablePast
                maxDate={getDateFromDayOffset(
                  apiTokenConfig?.maxExpirationDays,
                )}
                value={formikProps.values['expiresOn']}
                onChange={(newDate: any) =>
                  formikProps.setFieldValue('expiresOn', newDate)
                }
                renderInput={(params: TextFieldProps) => <TextField className={classes.input} {...params} />}
              />
            </MuiLocalizationProvider>
            <Accordion
              id="ApiToken::StandardTokens"
              title="Standard Tokens"
              initiallyExpanded
            >
              <Box>
                <Typography variant="body1">
                  Standard API tokens only have access to the following scopes.
                </Typography>
                {apiTokenConfig !== null && (
                  <ScopesList scopes={apiTokenConfig.defaultScopes} sort />
                )}
              </Box>
            </Accordion>
            <Accordion id="ApiToken::CustomTokens" title="Custom Tokens">
              <Box>
                <Typography variant="body1">
                  The following scopes can be made available on an as-needed
                  basis by doing the following.
                </Typography>
                <TokenCreationExplanation
                  bulletPoints={[
                    'Create a new token',
                    "Create a <a href='https://jira.centralsquare.com/secure/CreateIssue.jspa?issuetype=3&pid=10700' target='_blank' rel='noopener'>Task ticket in JIRA</a> referencing the new token ID and not the actual token.",
                    'Provide your reasons for needing the additional scopes for your API token.',
                    'The FieldOps team will review your request and approve or deny it based on your reasons.',
                  ]}
                />
                <ScopesList
                  scopes={DISALLOWED_STANDARD_API_TOKEN_SCOPES}
                  sort
                />
              </Box>
            </Accordion>
          </Box>
          <Box className={classes.spacer} />
          <EditModalActionBar
            canDelete={false}
            onDelete={() => {}}
            onCancel={onClose}
            formikProps={formikProps}
            saveText="Add"
          />
        </Form>
      )}
    </Formik>
  )
}
